import React, { useState } from 'react';
import { Play } from 'lucide-react';

interface YouTubeFacadeProps {
  videoId: string;
  title: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  rel?: boolean;
  modestbranding?: boolean;
  className?: string;
  thumbnailQuality?: 'default' | 'hqdefault' | 'mqdefault' | 'sddefault' | 'maxresdefault';
}

export const YouTubeFacade: React.FC<YouTubeFacadeProps> = ({
  videoId,
  title,
  autoplay = false,
  muted = false,
  loop = false,
  controls = true,
  rel = true,
  modestbranding = false,
  className = '',
  thumbnailQuality = 'maxresdefault'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const handleClick = () => {
    setIsLoaded(true);
  };

  // Build YouTube embed URL with all parameters
  const embedUrl = React.useMemo(() => {
    const params = new URLSearchParams();
    
    if (autoplay) params.append('autoplay', '1');
    if (muted) params.append('mute', '1');
    if (loop) {
      params.append('loop', '1');
      params.append('playlist', videoId); // Required for loop to work
    }
    if (!controls) params.append('controls', '0');
    if (!rel) params.append('rel', '0');
    if (modestbranding) params.append('modestbranding', '1');

    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  }, [videoId, autoplay, muted, loop, controls, rel, modestbranding]);

  if (isLoaded) {
    return (
      <iframe
        width="100%"
        height="100%"
        src={embedUrl}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        className={className}
      />
    );
  }

  return (
    <div 
      className={`relative cursor-pointer group ${className}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`Play video: ${title}`}
    >
      {/* Thumbnail Image */}
      <img
        src={`https://img.youtube.com/vi/${videoId}/${thumbnailQuality}.jpg`}
        alt={title}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      
      {/* Play Button Overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors duration-200">
        <div className="bg-red-600 hover:bg-red-700 rounded-full p-4 shadow-lg transform group-hover:scale-110 transition-all duration-200">
          <Play className="w-8 h-8 text-white ml-1" fill="white" />
        </div>
      </div>
      
      {/* YouTube Branding (optional) */}
      <div className="absolute bottom-2 right-2 opacity-75">
        <span className="text-xs text-white font-semibold px-2 py-1 bg-black/50 rounded">
          YouTube
        </span>
      </div>
    </div>
  );
};